import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const userData = await login(email, password);
            // If partner must change their temporary password on first login
            if (userData?.must_change_password) {
                navigate('/change-password');
            } else if (userData?.is_admin) {
                navigate('/admin');
            } else if (userData?.is_partner) {
                navigate('/partner/dashboard');
            } else {
                navigate('/');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="main" style={{ paddingTop: 0 }}>
            {/* Gradient Header Banner */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(222, 65, 176, 0.9) 0%, rgba(254, 205, 109, 0.9) 50%, rgba(255, 252, 242, 0.9) 100%)',
                height: '120px',
                marginBottom: '32px'
            }} />

            <div style={{
                minHeight: 'calc(100vh - 200px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px'
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: '560px',
                    minWidth: '250px'
                }}>
                    {/* Logo */}
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <Link to="/">
                            <img
                                src="/atam-logo.svg"
                                alt="ATAM"
                                style={{ height: '80px', width: 'auto' }}
                            />
                        </Link>
                    </div>

                    {/* Card */}
                    <div className="card" style={{ padding: '40px' }}>
                        <h1 style={{
                            fontSize: '28px',
                            fontWeight: '700',
                            marginBottom: '8px',
                            textAlign: 'center'
                        }}>
                            Welcome Back
                        </h1>
                        <p style={{
                            color: 'var(--text-secondary)',
                            textAlign: 'center',
                            marginBottom: '32px'
                        }}>
                            Sign in to your account to continue
                        </p>

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label">Email</label>
                                <div style={{ position: 'relative' }}>
                                    <Mail
                                        size={18}
                                        style={{
                                            position: 'absolute',
                                            left: '14px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: 'var(--text-muted)'
                                        }}
                                    />
                                    <input
                                        type="email"
                                        className="form-input"
                                        style={{ paddingLeft: '44px' }}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        autoComplete="off"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                    <label className="form-label" style={{ marginBottom: 0 }}>Password</label>
                                    <Link
                                        to="/reset-password"
                                        style={{
                                            fontSize: '13px',
                                            color: 'var(--primary)',
                                            textDecoration: 'none',
                                            fontWeight: 500
                                        }}
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                                <div style={{ position: 'relative' }}>
                                    <Lock
                                        size={18}
                                        style={{
                                            position: 'absolute',
                                            left: '14px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: 'var(--text-muted)'
                                        }}
                                    />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-input"
                                        style={{ paddingLeft: '44px', paddingRight: '44px' }}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        autoComplete="new-password"
                                        required
                                        minLength={6}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(p => !p)}
                                        style={{
                                            position: 'absolute',
                                            right: '14px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--text-muted)',
                                            padding: 0,
                                            display: 'flex'
                                        }}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="form-error" style={{ marginBottom: '16px' }}>
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary"
                                style={{ width: '100%' }}
                                disabled={loading}
                            >
                                {loading ? 'Please wait...' : 'Sign In'}
                            </button>
                        </form>

                        {/* Demo Admin Credentials - Hidden */}
                    </div>
                </div>
            </div>
        </main>
    );
}
